import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Brain, 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Upload, 
  FileText, 
  BookOpen, 
  Lightbulb,
  Target,
  GraduationCap,
  X,
  Save,
  Eye,
  Download,
  Filter,
  Tag,
  Clock,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { collection, getDocs, addDoc, updateDoc, doc, deleteDoc, query, orderBy, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import FileUpload, { useFileUpload } from '../../components/FileUpload';
import { formatTimestamp } from '../../utils/timestampUtils';
import toast from 'react-hot-toast';

const AIKnowledgeManagement = () => {
  const [knowledgeItems, setKnowledgeItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterSubject, setFilterSubject] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [viewingItem, setViewingItem] = useState(null);
  const [uploading, setUploading] = useState(false);

  const {
    uploadedFiles,
    uploading: fileUploading,
    progress,
    uploadFile,
    removeFile,
    setUploadedFiles
  } = useFileUpload({ storagePath: 'ai-knowledge' });

  const [newKnowledge, setNewKnowledge] = useState({
    title: '',
    type: 'concept', // concept, strategy, study_tip, exam_prep, subject_knowledge
    subject: '',
    exam: '',
    class: '',
    content: '',
    tags: [],
    category: 'general',
    priority: 'medium'
  });

  const knowledgeTypes = [
    { value: 'concept', label: 'Concept Explanation', icon: BookOpen, color: 'bg-blue-100 text-blue-800' },
    { value: 'strategy', label: 'Study Strategy', icon: Target, color: 'bg-green-100 text-green-800' },
    { value: 'study_tip', label: 'Study Tip', icon: Lightbulb, color: 'bg-yellow-100 text-yellow-800' },
    { value: 'exam_prep', label: 'Exam Preparation', icon: GraduationCap, color: 'bg-purple-100 text-purple-800' },
    { value: 'subject_knowledge', label: 'Subject Knowledge', icon: Brain, color: 'bg-indigo-100 text-indigo-800' },
    { value: 'timetable', label: 'Timetable Guide', icon: Clock, color: 'bg-orange-100 text-orange-800' }
  ];

  const subjects = ['Mathematics', 'Physics', 'Chemistry', 'Biology', 'English', 'History', 'Geography', 'Computer Science', 'General'];
  const exams = ['JEE', 'NEET', 'Board Exams', 'UPSC', 'General'];
  const classes = ['9', '10', '11', '12', 'General'];

  useEffect(() => {
    loadKnowledgeItems();
  }, []);

  const loadKnowledgeItems = async () => {
    try {
      setLoading(true);
      const knowledgeRef = collection(db, 'ai_knowledge');
      const knowledgeSnapshot = await getDocs(query(knowledgeRef, orderBy('createdAt', 'desc')));
      const items = knowledgeSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setKnowledgeItems(items);
    } catch (error) {
      toast.error('Failed to load knowledge items');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveKnowledge = async () => {
    if (!newKnowledge.title.trim() || !newKnowledge.content.trim()) {
      toast.error('Please fill in title and content');
      return;
    }

    try {
      setUploading(true);
      const knowledgeData = {
        ...newKnowledge,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        author: 'Admin',
        status: 'active',
        attachments: uploadedFiles || [],
        embeddings_processed: false // Will be processed by backend
      };

      let docId = editingItem?.id;
      
      if (editingItem) {
        await updateDoc(doc(db, 'ai_knowledge', editingItem.id), {
          ...knowledgeData,
          updatedAt: new Date().toISOString()
        });
        toast.success('Knowledge updated successfully');
        docId = editingItem.id;
      } else {
        const docRef = await addDoc(collection(db, 'ai_knowledge'), knowledgeData);
        docId = docRef.id;
        toast.success('Knowledge added successfully');
      }

      // Trigger backend to process and create embeddings (async, don't wait)
      processKnowledgeItem({ ...knowledgeData, id: docId }).catch(err => {
        // Don't show error to user - embeddings can be processed later
      });

      // Reset form
      setNewKnowledge({
        title: '',
        type: 'concept',
        subject: '',
        exam: '',
        class: '',
        content: '',
        tags: [],
        category: 'general',
        priority: 'medium'
      });
      setUploadedFiles([]);
      setShowAddModal(false);
      setEditingItem(null);
      await loadKnowledgeItems();
    } catch (error) {
      toast.error('Failed to save knowledge');
    } finally {
      setUploading(false);
    }
  };

  const processKnowledgeItem = async (knowledgeData) => {
    try {
      // Get the document ID if it exists
      let docId = knowledgeData.id;
      if (!docId && editingItem) {
        docId = editingItem.id;
      }
      
      const response = await fetch(`${process.env.REACT_APP_GATEWAY_URL || 'http://localhost:4000'}/api/knowledge/ingest`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          knowledge_id: docId || `kb_${Date.now()}`,
          title: knowledgeData.title,
          content: knowledgeData.content,
          type: knowledgeData.type,
          subject: knowledgeData.subject || '',
          exam: knowledgeData.exam || '',
          class: knowledgeData.class || '',
          tags: knowledgeData.tags || []
        })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && docId) {
          // Update Firestore with embedding status
          await updateDoc(doc(db, 'ai_knowledge', docId), {
            embeddings_processed: true,
            embedding_id: result.embedding_id || result.embedding_ids?.[0],
            updatedAt: new Date().toISOString()
          });
          toast.success('Knowledge processed and added to AI memory!');
        }
      }
    } catch (error) {
      // Don't show error to user - embeddings can be processed later
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Are you sure you want to delete "${item.title}"?`)) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'ai_knowledge', item.id));
      toast.success('Knowledge deleted successfully');
      await loadKnowledgeItems();
    } catch (error) {
      toast.error('Failed to delete knowledge');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setNewKnowledge({
      title: item.title || '',
      type: item.type || 'concept',
      subject: item.subject || '',
      exam: item.exam || '',
      class: item.class || '',
      content: item.content || '',
      tags: item.tags || [],
      category: item.category || 'general',
      priority: item.priority || 'medium'
    });
    setUploadedFiles(item.attachments || []);
    setShowAddModal(true);
  };

  const filteredItems = knowledgeItems.filter(item => {
    const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (item.tags && item.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())));
    const matchesType = filterType === 'all' || item.type === filterType;
    const matchesSubject = filterSubject === 'all' || item.subject === filterSubject;
    return matchesSearch && matchesType && matchesSubject;
  });

  const getTypeInfo = (type) => {
    return knowledgeTypes.find(t => t.value === type) || knowledgeTypes[0];
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
                <Brain className="w-8 h-8 mr-3 text-purple-600" />
                AI Knowledge Management
              </h1>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Teach the AI tutor by adding concepts, strategies, and knowledge
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setEditingItem(null);
                  setNewKnowledge({
                    title: '',
                    type: 'concept',
                    subject: '',
                    exam: '',
                    class: '',
                    content: '',
                    tags: [],
                    category: 'general',
                    priority: 'medium'
                  });
                  setUploadedFiles([]);
                  setShowAddModal(true);
                }}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center"
              >
                <Plus className="w-5 h-5 mr-2" />
                Add Knowledge
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search knowledge..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="all">All Types</option>
              {knowledgeTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
            <select
              value={filterSubject}
              onChange={(e) => setFilterSubject(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="all">All Subjects</option>
              {subjects.map(subject => (
                <option key={subject} value={subject}>{subject}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Knowledge List */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredItems.map((item) => {
              const typeInfo = getTypeInfo(item.type);
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6 hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <div className={`p-2 rounded-lg ${typeInfo.color}`}>
                        {React.createElement(typeInfo.icon, { className: "w-5 h-5" })}
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${typeInfo.color}`}>
                        {typeInfo.label}
                      </span>
                    </div>
                    {item.embeddings_processed ? (
                      <CheckCircle className="w-5 h-5 text-green-500" title="Processed for AI" />
                    ) : (
                      <XCircle className="w-5 h-5 text-yellow-500" title="Pending processing" />
                    )}
                  </div>
                  
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {item.title}
                  </h3>
                  
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-3">
                    {item.content}
                  </p>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {item.subject && (
                      <span className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 rounded">
                        {item.subject}
                      </span>
                    )}
                    {item.exam && (
                      <span className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 rounded">
                        {item.exam}
                      </span>
                    )}
                    {item.tags && item.tags.slice(0, 2).map((tag, idx) => (
                      <span key={idx} className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-400 rounded">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>{formatTimestamp(item.createdAt, { includeTime: false, short: true })}</span>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setViewingItem(item)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(item)}
                        className="text-green-600 hover:text-green-800"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(item)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {!loading && filteredItems.length === 0 && (
          <div className="text-center py-12">
            <Brain className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No knowledge items found
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Start teaching the AI by adding knowledge items
            </p>
            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              Add Knowledge
            </button>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {editingItem ? 'Edit Knowledge' : 'Add New Knowledge'}
              </h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setEditingItem(null);
                  setUploadedFiles([]);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Type *
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {knowledgeTypes.map(type => (
                    <button
                      key={type.value}
                      onClick={() => setNewKnowledge({ ...newKnowledge, type: type.value })}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        newKnowledge.type === type.value
                          ? 'border-purple-600 bg-purple-50 dark:bg-purple-900/20'
                          : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                      }`}
                    >
                      {React.createElement(type.icon, { className: "w-5 h-5 mx-auto mb-1" })}
                      <div className="text-xs font-medium">{type.label}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={newKnowledge.title}
                  onChange={(e) => setNewKnowledge({ ...newKnowledge, title: e.target.value })}
                  placeholder="e.g., How to create a study timetable"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Subject
                  </label>
                  <select
                    value={newKnowledge.subject}
                    onChange={(e) => setNewKnowledge({ ...newKnowledge, subject: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select Subject</option>
                    {subjects.map(subject => (
                      <option key={subject} value={subject}>{subject}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Exam
                  </label>
                  <select
                    value={newKnowledge.exam}
                    onChange={(e) => setNewKnowledge({ ...newKnowledge, exam: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select Exam</option>
                    {exams.map(exam => (
                      <option key={exam} value={exam}>{exam}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Class
                  </label>
                  <select
                    value={newKnowledge.class}
                    onChange={(e) => setNewKnowledge({ ...newKnowledge, class: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select Class</option>
                    {classes.map(cls => (
                      <option key={cls} value={cls}>{cls}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Content * (This is what the AI will learn)
                </label>
                <textarea
                  rows={10}
                  value={newKnowledge.content}
                  onChange={(e) => setNewKnowledge({ ...newKnowledge, content: e.target.value })}
                  placeholder="Enter detailed content, explanations, strategies, or knowledge that you want the AI to learn and use when answering questions..."
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Be detailed and comprehensive. The AI will use this to answer related questions.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Tags (comma-separated)
                </label>
                <input
                  type="text"
                  value={newKnowledge.tags.join(', ')}
                  onChange={(e) => setNewKnowledge({ 
                    ...newKnowledge, 
                    tags: e.target.value.split(',').map(t => t.trim()).filter(t => t) 
                  })}
                  placeholder="e.g., timetable, study plan, JEE, physics"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Attachments (Optional)
                </label>
                <FileUpload
                  onFilesUploaded={setUploadedFiles}
                  multiple={true}
                  maxFiles={5}
                  maxSize={10 * 1024 * 1024}
                  storagePath="ai-knowledge"
                  showPreview={true}
                  showProgress={true}
                  showActions={true}
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setEditingItem(null);
                  setUploadedFiles([]);
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveKnowledge}
                disabled={uploading || !newKnowledge.title.trim() || !newKnowledge.content.trim()}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {editingItem ? 'Update' : 'Save'} Knowledge
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
      {/* View Modal */}
      {viewingItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {viewingItem.title}
              </h2>
              <button
                onClick={() => setViewingItem(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="prose dark:prose-invert max-w-none">
              <p className="whitespace-pre-wrap">{viewingItem.content}</p>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AIKnowledgeManagement;

